import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { writeAuditLog } from '../../lib/auditLogger';
import RefundSection from './RefundSection';

const CompanyDetail = () => {
    const { tenantId } = useParams();
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [trialActionLoading, setTrialActionLoading] = useState(false);
    const [showTrialModal, setShowTrialModal] = useState(false);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [trialParams, setTrialParams] = useState({ trialMonths: 1, trialUnlimited: false });
    const [extendMonths, setExtendMonths] = useState(1);

    const fetchTenant = async () => {
        try {
            const response = await apiClient.get(`/v1/admin/tenants/${tenantId}`);
            if (response.ok) {
                const data = await response.json();
                setTenant(data);
                // Record Audit Log: COMPANY_VIEW (no sensitive info)
                writeAuditLog({
                    action: 'COMPANY_VIEW',
                    resourceType: 'company',
                    resourceId: tenantId,
                    metadata: { source: 'admin_console' }
                });
            }
        } catch (error) {
            console.error("Error fetching tenant detail:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTenant();
    }, [tenantId]);

    const handleSuspend = async () => {
        const reason = window.prompt('利用停止の理由を入力してください', '規約違反のため');
        if (reason === null) return;
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/suspend`, { reason });
            if (response.ok) {
                setTenant(prev => ({ ...prev, status: 'suspended' }));
                alert('利用停止しました。');
            }
        } catch (error) {
            console.error("Error suspending tenant:", error);
        }
    };

    const handleResume = async () => {
        const reason = window.prompt('利用再開の理由を入力してください', '誤停止の解除');
        if (reason === null) return;
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/resume`, { reason });
            if (response.ok) {
                setTenant(prev => ({ ...prev, status: 'active' }));
                alert('利用再開しました。');
            }
        } catch (error) {
            console.error("Error resuming tenant:", error);
        }
    };

    const handleInvoiceLock = async () => {
        const reason = window.prompt('請求書発行停止の理由を入力してください', '未払い調査のため');
        if (reason === null) return;
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/invoice-lock`, { reason });
            if (response.ok) {
                setTenant(prev => ({ ...prev, invoice_locked: true }));
                alert('請求書発行を停止しました。');
            }
        } catch (error) {
            console.error("Error locking invoice:", error);
        }
    };

    const handleInvoiceUnlock = async () => {
        const reason = window.prompt('請求書発行再開の理由を入力してください', '解消済み');
        if (reason === null) return;
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/invoice-unlock`, { reason });
            if (response.ok) {
                setTenant(prev => ({ ...prev, invoice_locked: false }));
                alert('請求書発行を再開しました。');
            }
        } catch (error) {
            console.error("Error unlocking invoice:", error);
        }
    };

    const handleStartTrial = async () => {
        setTrialActionLoading(true);
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/start-trial`, trialParams);
            if (response.ok) {
                alert('トライアルを開始しました。');
                setShowTrialModal(false);
                fetchTenant();
            }
        } catch (error) {
            console.error("Error starting trial:", error);
        } finally {
            setTrialActionLoading(false);
        }
    };

    const handleExtendTrial = async () => {
        setTrialActionLoading(true);
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/extend-trial`, { additionalMonths: extendMonths });
            if (response.ok) {
                alert('トライアルを延長しました。');
                setShowExtendModal(false);
                fetchTenant();
            }
        } catch (error) {
            console.error("Error extending trial:", error);
        } finally {
            setTrialActionLoading(false);
        }
    };

    const handleEndTrial = async () => {
        if (!window.confirm('トライアルを終了（次対象期間からFreeプランへ移行）しますか？')) return;
        setTrialActionLoading(true);
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/end-trial`);
            if (response.ok) {
                alert('トライアル終了を予約しました。');
                fetchTenant();
            }
        } catch (error) {
            console.error("Error ending trial:", error);
        } finally {
            setTrialActionLoading(false);
        }
    };

    if (loading) return <div className="p-8">読み込み中...</div>;
    if (!tenant) return <div className="p-8">企業が見つかりません。</div>;

    const planDisplay = {
        trial: 'トライアル',
        free: 'フリー',
        lite: 'ライト',
        standard: 'スタンダード',
        business: 'ビジネス'
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold">企業詳細: {tenant.company_name}</h2>
                <Link to="/admin/companies" className="text-indigo-600 hover:text-indigo-900">一覧に戻る</Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 基本情報 */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">基本情報</h3>
                    <dl className="grid grid-cols-1 gap-y-3">
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">会社名</dt>
                            <dd className="text-sm text-gray-900">{tenant.company_name}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">BillWaveお客様ID</dt>
                            <dd className="text-sm text-gray-900">{tenant.billwave_customer_id}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">StripeアカウントID</dt>
                            <dd className="text-sm text-gray-900">{tenant.stripe_account_id || '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">現在のプラン</dt>
                            <dd className="text-sm text-gray-900 font-bold">{planDisplay[tenant.plan] || tenant.plan}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">次対象期間適用プラン</dt>
                            <dd className="text-sm text-gray-900">{planDisplay[tenant.nextApplicablePlan] || tenant.nextApplicablePlan || '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">利用開始日</dt>
                            <dd className="text-sm text-gray-900">{tenant.started_at ? new Date(tenant.started_at).toLocaleDateString() : '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">ステータス</dt>
                            <dd className="text-sm">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    tenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                    {tenant.status}
                                </span>
                            </dd>
                        </div>
                    </dl>
                </div>

                {/* トライアル情報 */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">トライアル情報</h3>
                    <dl className="grid grid-cols-1 gap-y-3">
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">トライアル利用履歴</dt>
                            <dd className="text-sm text-gray-900">{tenant.hasUsedTrial ? 'あり' : 'なし'}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">トライアル種別</dt>
                            <dd className="text-sm text-gray-900">{tenant.trialSource === 'signup' ? '新規登録' : tenant.trialSource === 'admin' ? '管理者付与' : '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">トライアル開始日</dt>
                            <dd className="text-sm text-gray-900">{tenant.trialStartedAt ? new Date(tenant.trialStartedAt).toLocaleDateString() : '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">トライアル終了日</dt>
                            <dd className="text-sm text-gray-900">
                                {tenant.trialUnlimited ? '無期限' : (tenant.trialEndsAt ? new Date(tenant.trialEndsAt).toLocaleDateString() : '-')}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">設定対象期間数</dt>
                            <dd className="text-sm text-gray-900">{tenant.trialMonths ? `${tenant.trialMonths}ヶ月` : '-'}</dd>
                        </div>
                    </dl>
                    
                    <div className="mt-6 flex flex-wrap gap-2">
                        {tenant.plan !== 'trial' ? (
                            <button 
                                onClick={() => setShowTrialModal(true)}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                            >
                                トライアル開始
                            </button>
                        ) : (
                            <>
                                <button 
                                    onClick={() => setShowExtendModal(true)}
                                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                >
                                    トライアル延長
                                </button>
                                <button 
                                    onClick={handleEndTrial}
                                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                                >
                                    トライアル終了予約
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* 利用状況サマリー */}
            <div className="mt-8 bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">利用状況サマリー</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                    <div className="p-4 border rounded">
                        <dt className="text-xs font-medium text-gray-500 uppercase">チーム利用人数</dt>
                        <dd className="text-2xl font-bold text-gray-900">{tenant.team_member_count}名</dd>
                    </div>
                    <div className="p-4 border rounded">
                        <dt className="text-xs font-medium text-gray-500 uppercase">対象期間請求書発行件数</dt>
                        <dd className="text-2xl font-bold text-gray-900">{tenant.monthly_invoice_count}件</dd>
                    </div>
                    <div className="p-4 border rounded">
                        <dt className="text-xs font-medium text-gray-500 uppercase">プラン上限件数</dt>
                        <dd className="text-2xl font-bold text-gray-900">{tenant.monthly_plan_limit === Infinity ? '無制限' : `${tenant.monthly_plan_limit}件`}</dd>
                    </div>
                    <div className="p-4 border rounded">
                        <dt className="text-xs font-medium text-gray-500 uppercase">上限超過件数</dt>
                        <dd className="text-2xl font-bold text-red-600">{tenant.overage_count}件</dd>
                    </div>
                </dl>
            </div>

            {/* 返金セクション */}
            <RefundSection tenantId={tenantId} tenant={tenant} />

            {/* 利用制限 */}
            <div className="mt-8 bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">利用制限</h3>
                <div className="flex flex-wrap gap-4">
                    {tenant.status === 'active' ? (
                        <button onClick={handleSuspend} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                            利用停止にする
                        </button>
                    ) : (
                        <button onClick={handleResume} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                            利用を再開する
                        </button>
                    )}

                    {tenant.invoice_locked ? (
                        <button onClick={handleInvoiceUnlock} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                            請求書発行を再開する
                        </button>
                    ) : (
                        <button onClick={handleInvoiceLock} className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700">
                            請求書発行を停止する
                        </button>
                    )}
                </div>
            </div>

            {/* 関連情報リンク */}
            <div className="mt-8 bg-gray-50 shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">関連情報</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Link 
                        to={`/admin/csv-history?billwaveCustomerId=${tenant.tenant_id}`}
                        className="bg-white border p-4 rounded text-center hover:bg-gray-100 block"
                    >
                        CSV履歴を開く
                    </Link>
                    <Link 
                        to={`/admin/stripe-logs?billwaveCustomerId=${tenant.tenant_id}`}
                        className="bg-white border p-4 rounded text-center hover:bg-gray-100 block"
                    >
                        Stripe連携ログを開く
                    </Link>
                    <Link 
                        to={`/admin/activity?billwaveCustomerId=${tenant.tenant_id}`}
                        className="bg-white border p-4 rounded text-center hover:bg-gray-100 block"
                    >
                        操作履歴を開く
                    </Link>
                </div>
            </div>

            {/* Start Trial Modal */}
            {showTrialModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-md">
                        <h3 className="text-lg font-bold mb-4 border-b pb-2">トライアル開始設定</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">有効月数</label>
                                <input 
                                    type="number" 
                                    min="1" 
                                    value={trialParams.trialMonths}
                                    onChange={(e) => setTrialParams({ ...trialParams, trialMonths: e.target.value })}
                                    disabled={trialParams.trialUnlimited}
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                />
                            </div>
                            <div className="flex items-center">
                                <input 
                                    type="checkbox" 
                                    id="unlimited"
                                    checked={trialParams.trialUnlimited}
                                    onChange={(e) => setTrialParams({ ...trialParams, trialUnlimited: e.target.checked })}
                                    className="h-4 w-4 text-indigo-600 rounded"
                                />
                                <label htmlFor="unlimited" className="ml-2 block text-sm text-gray-900">無期限トライアル</label>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setShowTrialModal(false)} className="px-4 py-2 bg-gray-200 rounded">キャンセル</button>
                            <button 
                                onClick={handleStartTrial} 
                                disabled={trialActionLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                            >
                                {trialActionLoading ? '実行中...' : '開始する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Extend Trial Modal */}
            {showExtendModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-bold mb-4 border-b pb-2">トライアル延長</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">追加月数</label>
                            <input 
                                type="number" 
                                min="1" 
                                value={extendMonths}
                                onChange={(e) => setExtendMonths(e.target.value)}
                                className="mt-1 block w-full border rounded-md shadow-sm p-2"
                            />
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setShowExtendModal(false)} className="px-4 py-2 bg-gray-200 rounded">キャンセル</button>
                            <button 
                                onClick={handleExtendTrial} 
                                disabled={trialActionLoading}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                            >
                                {trialActionLoading ? '実行中...' : '延長する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDetail;
